import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import CustomEventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_ERROR, EMITTER_SUCCESS } from "../../utils/constants";
import { INITIAL_ROUTE, INVALID_TOKEN_PATH, LOGIN_PATH } from "../../utils/constants/routes";
import {
  authEndpoints,
  signupEndpoints,
  HOST_URL,
  protocol,
} from "../../utils/constants/httpConstants";
import { encryptEmail } from "../../utils/helpers";
import { Strings } from "./strings";
import { POST } from "../../Apis/apiWrapper";
import WorkspacePreparationScreen from "../SignupScreen/WorkspacePreparationScreen";
import { CHECK_LOGIN_SUCCESS } from "../../redux/constants/authConstants";

// err is the axios RESPONSE object (rethrown by the POST wrapper), or undefined
// on a network/transport error (axios error had no `.response`). Returns a
// normalized { reason, message } so no backend error gets silently swallowed.
//
// Reason mapping (verify-email error matrix):
//   422 errors.email   -> "email_taken"       (account already registered)  -> Login
//   422 errors.domain  -> "domain_taken"      (workspace domain already registered) -> Login
//   422 errors.flow    -> "setup_incomplete"  (steps skipped)
//   422 errors.payment -> "payment_pending"   (paid plan unpaid)
//   400 message        -> "token_missing"     (verification_token absent)
//   422 message / else -> "invalid_token"     (token not found / truly invalid)
//   no response object -> "network"           (request never reached server)
const parseVerifyError = (err) => {
  // Network / transport failure: POST wrapper threw `error?.response` which was undefined.
  if (!err) return { reason: "network", message: Strings.SOMETHING_WENT_WRONG };

  const errors = err?.data?.data?.errors || {};
  const status = err?.status;

  // Already-registered cases (email or company/domain): the user has an account —
  // point them at login, not at a resend/retry. The backend now returns clear,
  // dedicated error keys for these instead of a generic "already exist" message.
  if (errors.email) return { reason: "email_taken", message: errors.email || Strings.VERIFY_EMAIL_TAKEN };
  if (errors.domain) return { reason: "domain_taken", message: Strings.VERIFY_DOMAIN_TAKEN };
  if (errors.flow) return { reason: "setup_incomplete", message: Strings.VERIFY_SETUP_INCOMPLETE };
  if (errors.payment) return { reason: "payment_pending", message: Strings.VERIFY_PAYMENT_PENDING };

  // A 400 with a general message is the "required field" / missing-token case.
  if (status === 400) {
    return { reason: "token_missing", message: err?.data?.message || Strings.VERIFY_TOKEN_MISSING };
  }

  // Any other field error we don't have a dedicated branch for: surface it rather
  // than swallow it (guards against future-added error fields).
  const firstFieldError = Object.values(errors).find(Boolean);
  if (firstFieldError) {
    return { reason: "unknown_field", message: firstFieldError };
  }

  // Genuinely invalid / expired token (422 "Something went wrong" or anything else).
  return { reason: "invalid_token", message: err?.data?.message || Strings.INVALID_TOKEN_DESC };
};

const EmailVerification = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  let isExistTenant =
    (location?.search?.slice(1) || "") === Strings.Exist_TENANT ? true : false;
  const token = params?.id;

  const createUrl = (domain, email) => {
    let subDomain = domain?.trim().split(".")[0];
    const encryptedEmail = encryptEmail(email);
    let redirectedUrl = `${protocol}${subDomain}.${HOST_URL}${LOGIN_PATH}/${encryptedEmail}`;
    window.location.href = redirectedUrl;
  };

  const redirectToTenant = (tenantUrl, userData) => {
    // Store user data in Redux store (similar to login flow)
    dispatch({
      type: CHECK_LOGIN_SUCCESS,
      payload: {
        status: true,
        data: userData
      }
    });

    // Redirect to onboarding form on tenant subdomain
    let subDomain = tenantUrl?.trim().split(".")[0];
    let redirectedUrl = `${protocol}${subDomain}.${HOST_URL}${INITIAL_ROUTE}`;
    window.location.href = redirectedUrl;
  };

  useEffect(() => {
    (async () => {
      try {
        if (isExistTenant) {
          // ── Existing-tenant user verification (old flow) ──────────────────
          const res = await POST(authEndpoints.verifyUserEmail, { verification_token: token });
          if (res) {
            CustomEventEmitter.emit(EMITTER_SUCCESS, res?.data?.message || Strings.EMAIL_VERIFIED);
            setTimeout(() => {
              if (res?.data?.data?.user_data) {
                redirectToTenant(res?.data?.data?.tenant_url, res?.data?.data?.user_data);
              } else {
                createUrl(res?.data?.data?.tenant_url, res?.data?.data?.email);
              }
            }, 1000);
          }
        } else {
          // ── New signup verification (v2 onboarding flow) ──────────────────
          const res = await POST(signupEndpoints.verifyEmail, { verification_token: token });
          if (res) {
            CustomEventEmitter.emit(EMITTER_SUCCESS, res?.data?.message || Strings.EMAIL_VERIFIED_SETUP);
            const tenantUrl = res?.data?.data?.tenant_url;  // e.g. "resourcifi.reachiq.ai"
            const email     = res?.data?.data?.email;
            // Keep the workspace-preparation loader visible for ~3s (this screen
            // already renders it), then go STRAIGHT to the tenant login. The old
            // intermediate /auth/workspace-setup hop has been removed — it replayed
            // the same loader a second time. createUrl builds the cross-subdomain
            // tenant login URL with the email pre-filled.
            setTimeout(() => {
              createUrl(tenantUrl, email);
            }, 3000);
          }
        }
      } catch (err) {
        // err is the axios response object (from the POST wrapper), or undefined
        // on a network error. Branch on the backend reason instead of always
        // showing the generic "Token Expired!" screen.
        const { reason, message } = parseVerifyError(err);

        // "setup_incomplete" is not a bad token — the user skipped a step. The
        // error payload carries tenant_url/email, the same data the success path
        // forwards. Keep the loader up briefly, then send them straight to the
        // tenant login (same destination as success; the workspace-setup hop is gone).
        if (reason === "setup_incomplete") {
          const tenantUrl = err?.data?.data?.tenant_url;
          const email = err?.data?.data?.email;
          setTimeout(() => {
            createUrl(tenantUrl, email);
          }, 3000);
          return; // leave loading=true so the prep loader stays visible until redirect
        }

        // Already registered (email or company/domain): the user has an account —
        // send them to login, not to the invalid-token page (and never offer resend).
        // If the backend gives us the tenant_url, go to that tenant's login with the
        // email pre-filled; otherwise fall back to the generic login route.
        if (reason === "email_taken" || reason === "domain_taken") {
          CustomEventEmitter.emit(EMITTER_ERROR, message);
          const tenantUrl = err?.data?.data?.tenant_url;
          const email = err?.data?.data?.email;
          if (tenantUrl) {
            setLoading(false);
            createUrl(tenantUrl, email);
          } else {
            setLoading(false);
            navigate(LOGIN_PATH);
          }
          return;
        }

        // Surface the actionable reason as a toast (global EMITTER_ERROR listener),
        // so the user understands WHY verification failed rather than just seeing
        // a blanket "Token Expired!" page.
        if (reason !== "invalid_token") {
          CustomEventEmitter.emit(EMITTER_ERROR, message);
        }

        // Only a genuinely missing/invalid/expired token (or an unrecoverable
        // network/known-actionable failure) lands on the full-screen InvalidToken
        // page. The toast above carries the specific reason across the redirect
        // since the listener is mounted globally. Flip loading off so we leave the
        // loader before navigating away.
        setLoading(false);
        navigate(INVALID_TOKEN_PATH);
      }
    })();
  }, [navigate, token, isExistTenant]);

  // This screen verifies the token and then redirects (success → workspace setup,
  // failure → InvalidToken page, with a toast carrying the specific reason). While
  // working it shows the full workspace-preparation loader UI (logo + module pills +
  // progress bar) instead of a bare spinner. EmailVerification owns the post-verify
  // navigation, so the prep screen is rendered with redirectOnComplete={false} — it's
  // used here purely as the loading visual and must not fire its own redirect.
  return (
    <Box role="status" aria-live="polite" aria-busy={loading} aria-label="Verifying your email">
      {loading ? <WorkspacePreparationScreen /> : null}
    </Box>
  );
};

export default EmailVerification;
